import React from 'react';
import { useTranslation } from 'react-i18next';
import type { EducationAnalysis } from '../../types';

interface EducationSectionProps {
  data: EducationAnalysis;
}

const EducationSection: React.FC<EducationSectionProps> = ({ data }) => {
  const { t } = useTranslation();
  const formatEducationLevel = (level: string | null): string => {
    if (!level) return t('results.education.unspecified');
    
    switch (level) {
      case 'phd': return t('results.education.level.phd');
      case 'master': return t('results.education.level.master');
      case 'bachelor': return t('results.education.level.bachelor');
      case 'diploma': return t('results.education.level.diploma');
      case 'high_school': return t('results.education.level.high_school');
      default: return level;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-5 flex items-center">
        <span className="text-2xl mr-3">🎓</span>
        <h3 className="text-xl font-semibold">{t('results.education.title')}</h3>
        <span className="ml-auto text-xl font-bold">{data?.score}%</span>
      </div>
      
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-semibold text-lg mb-4 text-gray-800">{t('results.education.levelTitle')}</h4>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">{t('results.education.requiredLevel')}:</span>
                <span className="font-medium text-gray-900">
                  {formatEducationLevel(data?.requiredLevel)}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-700">{t('results.education.yourLevel')}:</span>
                <span className={`font-medium ${data?.analysis?.levelMatch ? 'text-green-600' : 'text-orange-600'}`}>
                  {formatEducationLevel(data?.cvEducation?.level)}
                </span>
              </div>
              
              <div className="pt-3">
                <div className="flex items-center">
                  <span className={`text-2xl mr-2 ${data?.analysis?.levelMatch ? 'text-green-500' : 'text-amber-500'}`}>
                    {data?.analysis?.levelMatch ? '✓' : '⚠️'}
                  </span>
                  <span>
                    {data?.analysis?.levelMatch 
                      ? t('results.education.levelMatch')
                      : t('results.education.levelMismatch')}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-semibold text-lg mb-4 text-gray-800">{t('results.education.fieldTitle')}</h4>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">{t('results.education.requiredField')}:</span>
                <span className="font-medium text-gray-900">
                  {data?.requiredField || t('results.education.unspecified')}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-700">{t('results.education.yourField')}:</span>
                <span className={`font-medium ${data?.analysis?.fieldMatch ? 'text-green-600' : 'text-orange-600'}`}>
                  {data?.cvField || t('results.education.nonDetected')}
                </span>
              </div>
              
              <div className="pt-3">
                <div className="flex items-center">
                  <span className={`text-2xl mr-2 ${data?.analysis?.fieldMatch ? 'text-green-500' : 'text-amber-500'}`}>
                    {data?.analysis?.fieldMatch ? '✓' : '⚠️'}
                  </span>
                  <span>
                    {data?.analysis?.fieldMatch 
                      ? t('results.education.fieldMatch')
                      : t('results.education.fieldMismatch')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {data?.analysis?.degrees?.length > 0 && (
          <div className="mt-6">
            <h4 className="font-semibold text-lg mb-4 text-gray-800">{t('results.education.degreesTitle')}</h4>
            
            <div className="space-y-3">
              {data?.analysis?.degrees?.map((degree, index) => (
                <div 
                  key={index}
                  className="bg-gray-50 p-3 rounded-lg border-l-4 border-green-500"
                >
                  {degree}
                </div>
              ))}
            </div>
          </div>
        )}
        {data?.analysis?.degrees?.length === 0 && (
          <p className="text-gray-500 italic">{t('results.education.noDegrees')}</p>
        )}
      </div>
    </div>
  );
};

export default EducationSection;